import type { FC } from "react";
import React, { createContext, useEffect, useState } from "react";

import { useLocale } from "..";
import { useBrand } from "../brand";
import { useTranslationsManager } from "./TranslationsProvider";
import type {
  FetchTranslationsArgs,
  LocaleTranslations,
  NamespaceTranslations,
  TranslationLoaders,
  TranslationsPageProviderProps,
} from "./types";

export const InitializedTranslationsContext = createContext<boolean>(false);

const fetchTranslations = ({
  translationLoaders,
  brand,
  locale,
}: FetchTranslationsArgs): Promise<LocaleTranslations> => {
  const translationPromises = Object.entries(translationLoaders).map(
    ([namespace, translationLoader]) => {
      return translationLoader({ brand, locale })
        .then((nsTranslations): [string, NamespaceTranslations] => {
          return [namespace, nsTranslations.default];
        })
        .catch((): [string, NamespaceTranslations] => {
          return [namespace, {}];
        });
    },
  );

  return Promise.all(translationPromises).then((translationList) => {
    return translationList.reduce<LocaleTranslations>(
      (translations, [namespace, nsTranslations]) => {
        translations[namespace] = nsTranslations;
        return translations;
      },
      {},
    );
  });
};

const createTranslationsPageProvider = (
  translationLoaders: TranslationLoaders,
): FC<TranslationsPageProviderProps> => {
  const useAddResource = () => {
    const [initialized, setInitialized] = useState(false);
    const brand = useBrand();
    const locale = useLocale();
    const translationsManager = useTranslationsManager();

    useEffect(() => {
      let isMounted = true;
      fetchTranslations({ translationLoaders, brand, locale }).then(
        (translations) => {
          translationsManager.add(locale, { ...translations }, () => {
            if (isMounted) {
              setInitialized(true);
            }
          });
        },
      );
      return () => {
        isMounted = false;
      };
    }, [translationsManager, brand, locale]);
    return initialized;
  };

  return function TranslationsPageProvider({
    children,
  }: TranslationsPageProviderProps) {
    const initialized = useAddResource();
    return (
      <InitializedTranslationsContext.Provider value={initialized}>
        {children}
      </InitializedTranslationsContext.Provider>
    );
  };
};

export default createTranslationsPageProvider;
